import React, { useState } from 'react'
import { Col, Container, Row, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { TableColumn } from 'react-data-table-component'
import { useGetPresetsQuery, useGetPresetTagsQuery, useLazyGetFeaturesQuery } from '../../../store/ai/ai'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { Tag, TagElement } from '../../../components/Tag/Tag'
import styled from 'styled-components'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { AiFeaturePreset } from '../../../models/AiFeaturePreset'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { PresetFormConfigModal } from './PresetConfig/Form/PresetFormConfigModal'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { PresetTemplateConfigModal } from './PresetConfig/Template/PresetTemplateConfigModal'
import { AIFeaturePresetBusinessEvents } from './AIFeaturePresetBusinessEvents'
import { TemplateSelectRow } from '../../../components/InputParameterHierarchy/Template/TemplateSelectRow'
import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { SuccessToast } from '../../../components/SuccessToast/SuccessToast'
import { useLazyGetEffectiveNodeQuery } from '../../../store/ai/inputParametersHierarchy'
import { InputParametersHierarchyLevel } from '../../../models/InputParametersHierarchy'

const PresetIconWrapper = styled.div`
  display: inline-flex;
  padding: 0.75rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.25rem;
  background: var(--zocks-primary);
  color: #fff;

  i {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
  }

  img {
    width: 2rem;
    height: 2rem;
  }
`

export const PresetTab: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const { data, isFetching } = useGetPresetsQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, tag: selectedTag },
    { refetchOnMountOrArgChange: true },
  )
  const { data: tags } = useGetPresetTagsQuery({ accountId: accountId, pageRequest: { page: 0, size: 500 } })
  const [selectedPreset, setSelectedPreset] = useState<AiFeaturePreset | null>(null)
  const [showPresetConfigModal, setShowPresetConfigModal] = useState<boolean>(false)
  const [showDownloadToast, setShowDownloadToast] = useState<boolean>(false)

  const [getFeatures] = useLazyGetFeaturesQuery()
  const [getEffectiveNode] = useLazyGetEffectiveNodeQuery()

  const handleConfigOnClick = (preset: AiFeaturePreset) => {
    setSelectedPreset(preset)
    setShowPresetConfigModal(true)
  }

  const handleConfigModalHide = () => {
    setShowPresetConfigModal(false)
    setSelectedPreset(null)
  }

  const handleExportPreset = async (preset: AiFeaturePreset) => {
    const [featuresResult, effectiveNodeResult] = await Promise.all([
      getFeatures({ accountId, pageRequest: { page: 0, size: 500 }, includeRestricted: true }),
      getEffectiveNode({
        accountId,
        type: InputParameterHierarchyType.AI_FEATURE_PRESET,
        typeId: preset.id,
        context: { effective: true, level: InputParametersHierarchyLevel.GLOBAL },
      }),
    ])

    let inputParameters: Record<string, unknown>

    if (effectiveNodeResult.data) {
      inputParameters = effectiveNodeResult.data.inputParameters
    } else {
      const presetFeatures = featuresResult.data?.data.filter((feature) => feature.preset?.id === preset.id) ?? []
      const aiFeature = presetFeatures.find((feature) => !!feature.inputParameters)
      inputParameters = aiFeature?.inputParameters ?? {}
    }

    const isEmpty = Object.keys(inputParameters).length === 0
    if (isEmpty && preset.inputParametersSchema?.properties) {
      const defaults: Record<string, unknown> = {}
      for (const [key, schema] of Object.entries<{ default?: unknown }>(preset.inputParametersSchema.properties)) {
        if (schema.default !== undefined) {
          defaults[key] = schema.default
        }
      }
      inputParameters = defaults
    }

    const blob = new Blob(
      [
        JSON.stringify(
          {
            type: 'ai-feature-presets',
            typeId: preset.id,
            inputParameters: inputParameters,
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    )
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${preset.name.replace(/\s+/g, '_')}_preset.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setSelectedPreset(preset)
    setShowDownloadToast(true)
  }

  const columns: TableColumn<AiFeaturePreset>[] = [
    {
      name: 'Preset Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => {
        return (
          <div className='flex-grow-1 d-flex gap-3 align-items-center'>
            <PresetIconWrapper>
              <img src={row.assetUrl} alt='asset logo' />
            </PresetIconWrapper>
            <div className='d-flex flex-column'>
              <div className='button-link-medium text-decoration-none'>{row.name}</div>
              <AIFeaturePresetBusinessEvents aiFeaturePreset={row} />
            </div>
          </div>
        )
      },
    },
    {
      name: 'Description',
      wrap: true,
      grow: 2,
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: 'Tags',
      sortable: true,
      cell: (row) => (
        <div className='d-flex gap-2 flex-wrap'>
          {row.tags.map((tag) => (
            <TagElement key={`row-tag-element-${tag}`}>{tag}</TagElement>
          ))}
        </div>
      ),
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => (
        <>
          {row.inputParametersSchema?.properties && !row.inputParametersSchema?.properties?.template && row.installed ? (
            <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Export preset configuration</Tooltip>}>
              {({ ref, ...triggerHandler }) => (
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'aifeature:list',
                    superScope: ['aifeature:admin'],
                  }}
                >
                  <Button onClick={() => handleExportPreset(row)} {...triggerHandler}>
                    <i ref={ref} className='bi bi-download' />
                  </Button>
                </ScopedBlock>
              )}
            </OverlayTrigger>
          ) : null}
        </>
      ),
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => (
        <>
          {row.inputParametersSchema?.properties && !row.inputParametersSchema?.properties?.template && row.installed ? (
            <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Customize preset</Tooltip>}>
              {({ ref, ...triggerHandler }) => (
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'aifeature:list',
                    superScope: ['aifeature:admin'],
                  }}
                >
                  <Button onClick={() => handleConfigOnClick(row)} {...triggerHandler}>
                    <i ref={ref} className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              )}
            </OverlayTrigger>
          ) : null}
        </>
      ),
    },
  ]

  const handleFilterSelected = tracked(
    (tag: string | null) => setSelectedTag(tag),
    { eventName: TrackEvent.PRESET_FILTER_CLICKED },
    {
      eventDetailsExtractor: (tag) => ({ tag }),
    },
  )

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const ConfigModal = selectedPreset?.inputParametersSchema?.properties?.template ? PresetTemplateConfigModal : PresetFormConfigModal

  return (
    <Container fluid>
      {showPresetConfigModal && selectedPreset ? <ConfigModal show={showPresetConfigModal} onHide={handleConfigModalHide} preset={selectedPreset} /> : null}
      <SuccessToast
        position={'bottom-center'}
        message={`Preset "${selectedPreset?.name}" downloaded.`}
        show={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      />
      <Row className='d-flex gap-2 zocks-mb-2.5'>
        <Col xs='auto' className='d-flex gap-3'>
          <div className='d-flex gap-1 align-items-center'>
            <i className='bi bi-funnel'></i>
            <div className='lead-small'>Filter</div>
          </div>
        </Col>
        <Col xs='auto' className='m-0 p-0'>
          <Tag selected={selectedTag === null} onClick={() => handleFilterSelected(null)}>
            All
          </Tag>
        </Col>
        {tags &&
          tags.data.map((tag) => (
            <Col key={tag} xs='auto' className='p-0 m-0'>
              <Tag selected={tag === selectedTag} onClick={() => handleFilterSelected(tag)}>
                {tag}
              </Tag>
            </Col>
          ))}
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            conditionalRowStyles={[
              {
                when: (row) => Boolean(row?.inputParametersSchema?.properties?.template),
                style: {
                  borderBottomWidth: '0 !important',
                },
              },
            ]}
            expandableRows
            expandableRowsHideExpander
            expandableRowExpanded={() => true}
            expandableRowsComponent={PresetTemplateExpandableRow}
          />
        </Col>
      </Row>
    </Container>
  )
}

const PresetTemplateExpandableRow: React.FC<{ data: AiFeaturePreset }> = ({ data: preset }) => {
  const { hasScope } = useZocksAuth()
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  if (!preset.inputParametersSchema?.properties?.template) {
    return null
  }

  return (
    <Row className='mb-2 mx-0 px-3 border-bottom zocks-border-color-gray-200'>
      <Col className='p-0'>
        <h6 className='mb-1 mt-2'>Template</h6>
        <div className='paragraph-medium'>Choose which template to use for this preset. Your personal choice overrides the account default.</div>
        <Table className={`mt-4 templatesTable`}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Your choice</th>
              <th>Account default</th>
              {hasIpHierarchyAdminScope ? <th>User-specific</th> : null}
            </tr>
          </thead>
          <tbody>
            <TemplateSelectRow
              title={
                <>
                  {preset.name} <span className='zocks-text-gray-600 label-small'>(Preset)</span>
                  <OverlayTrigger
                    placement='top'
                    overlay={<BootstrapTooltip>This template is included with the ‘{preset.name}’ preset added to this meeting type.</BootstrapTooltip>}
                  >
                    <i className='bi bi-info-square zocks-text-gray-600 ps-1 label-large' />
                  </OverlayTrigger>
                </>
              }
              templateSelectionSubHeader={`For ${preset.name} preset`}
              type={InputParameterHierarchyType.AI_FEATURE_PRESET}
              typeId={preset.id}
              context={{
                userId: currentUserId,
                defaultTypeId: preset.id,
              }}
            />
          </tbody>
        </Table>
      </Col>
    </Row>
  )
}
